<template>
    <div v-if="!loading">
        <ui-card class="pt-4 pl-4 pr-6 pb-3 mb-5" v-for="app in registration.conference.applications" v-if="!appOpen || app.ready.toggle">
            <div class="columns is-gapless">
                <div class="column is-narrow is-size-6">
                    <i class="fad fa-fw fa-file-certificate fa-2x has-text-primary mr-5"></i>
                </div>
                <div class="column mr-5">
                    <div class="has-text-weight-bold is-size-5 mt-1">
                        {{ app.application.title }}
                        <b-tooltip label="Additional Fee Required" type="is-info" v-if="chargeAmount(app) > 0">
                            <b-tag type="is-warning is-light" class="ml-3 mr-3"><i class="fas fa-dollar"></i></b-tag>
                        </b-tooltip>

                        <b-tooltip label="Required Application" type="is-info" v-if="app.is_required_at_end && !getConfApp(app).completed_at">
                            <b-tag type="is-warning is-light" class="ml-3 mr-3"><i class="fas fa-exclamation-triangle mr-2"></i>Required</b-tag>
                        </b-tooltip>
                    </div>

                    <div class="is-size-6 has-text-grey mt-3">{{ app.application.description }}</div>

                    <div v-if="!app.is_enabled && !getConfApp(app).completed_at" class="mt-6">
                        <b-message type="is-warning">
                            <div>This application is not available at this time. Either it has not been made available yet (generally after the event and individual sessions have concluded), or it has expired. For the most accurate and up-to-date information, please contact the event organizer.</div>
                            <div class="mt-4 pr-6 has-text-weight-bold">
                                <i class="far fa-fw fa-arrow-right mr-2"></i>Please, check back later or contact the event organizer for more information.
                            </div>
                        </b-message>
                    </div>

                    <section v-else>
                        <div v-if="!getConfApp(app).id">
                            <div v-if="app.certification.type === 'unit'">
                                <div class="mt-5 pr-6 pb-2">You have recorded <strong>{{ calcUnits(app).toFixed(2) }}</strong> of <strong>{{ parseFloat(app.max_units).toFixed(2) }}</strong> available {{ app.certification.unit_title }}.</div>

                                <div v-if="calcUnits(app) > 0">
                                    <b-progress
                                        size="is-small"
                                        type="is-success"
                                        :value="calcUnits(app)"
                                        :max="parseFloat(app.max_units)"
                                        class="mb-4"
                                    ></b-progress>
                                    <div class="help"><i class="far fa-info-circle mr-1"></i> Your registration may not include all available sessions. Additional time may be earned from On-Demand Content, if available. Note that {{ app.certification.unit_title }} calculation may differ due to rounding.</div>
                                </div>
                                <div class="mt-3" v-else>
                                    <i class="far fa-fw fa-arrow-right mr-2"></i>Your log is empty. Add sessions from the <strong><i class="far fa-calendar-circle-user mr-2 ml-1"></i>Sessions & Schedules</strong> or watch <strong><i class="far fa-clapperboard-play mr-2 ml-1"></i>On-Demand Content</strong> to contribute to the log.
                                </div>
                            </div>

                            <hr/>

                            <div v-if="!app.ready.toggle">
                                <b-button type="is-info" @click="start(app)"><strong>Start Application</strong><i class="far fa-arrow-right-long ml-3"></i></b-button>
                            </div>

                            <div v-show="app.ready.toggle">
                                <div class="mt-6">
                                    <b-message type="is-info">
                                        <p class="has-text-weight-bold is-size-5">Are you ready to apply?</p>
                                        <p class="mb-5">Please review and check the following to verify that you are ready to apply for this certification:</p>

                                        <div class="columns is-gapless">
                                            <div class="column is-narrow">
                                                <b-switch type="is-success" class="mr-4" v-model="app.ready.sessions"></b-switch>
                                            </div>
                                            <div class="column">
                                                I have added all live sessions I attended from <strong><i class="far fa-calendar-circle-user mr-2 ml-1"></i>Sessions & Schedules</strong> to my schedule, if applicable.
                                            </div>
                                        </div>
                                        <div class="columns is-gapless">
                                            <div class="column is-narrow">
                                                <b-switch type="is-success" class="mr-4" v-model="app.ready.ondemand"></b-switch>
                                            </div>
                                            <div class="column">
                                                I have watched all <strong><i class="far fa-clapperboard-play mr-2 ml-1"></i>On-Demand Content</strong> I intend to, if applicable. Please note that not all On-Demand Content may be available immediately and may be added later. Please contact the event organizer for more information.
                                            </div>
                                        </div>
                                        <div class="columns is-gapless">
                                            <div class="column is-narrow">
                                                <b-switch type="is-success" class="mr-4" v-model="app.ready.log"></b-switch>
                                            </div>
                                            <div class="column">
                                                I have double-checked my <strong><i class="far fa-clipboard-list-check mr-2 ml-1"></i>Record & Attendance Log</strong> for accuracy and have verified that all sessions I attended (either live or on-demand) are listed and confirmed.
                                            </div>
                                        </div>
                                        <div class="columns is-gapless">
                                            <div class="column is-narrow">
                                                <b-switch type="is-success" class="mr-4" v-model="app.ready.confirm"></b-switch>
                                            </div>
                                            <div class="column">
                                                I understand that once I apply, <u>changes to my log will not make any changes to this application or this certification</u>. A fee may apply for a modification after the application is submitted. Application must be completed within 14 days once you apply.
                                            </div>
                                        </div>
                                        <section v-if="chargeAmount(app) > 0">
                                            <hr style="background: rgb(158 198 231)"/>
                                            <div class="columns is-gapless">

                                                <div class="column is-narrow">
                                                    <b-switch type="is-success" class="mr-4" v-model="app.ready.charge"></b-switch>
                                                </div>
                                                <div class="column">
                                                    <p v-if="app.certification.type === 'unit'">I understand the cost of this application is <strong>{{ chargeAmount(app) | currency }}</strong> for <strong>{{ calcUnits(app).toFixed(2) }} {{ app.certification.unit_title }}</strong>. This fee is separate from any registration fees, if applicable, and will be collected at the end of the application, if approved.</p>
                                                    <p v-else>I understand the cost of this application is <strong>{{ chargeAmount(app) | currency }}</strong> for <strong>{{ app.certification.unit_title }}</strong>. This fee is separate from registration fees, if applicable, and will be collected at the end of the application, if approved.</p>
                                                </div>
                                            </div>
                                        </section>

                                    </b-message>

                                    <b-message type="" v-if="app.min_minutes_required || app.min_sessions_required || app.req_sessions.length" class="mt-5">
                                        <section>
                                            <p class="has-text-weight-bold is-size-5 ">Other Requirements</p>

                                            <div v-if="app.min_minutes_required" class="mb-5">
                                                <div class="columns is-gapless" v-if="totalMinutes < app.min_minutes_required">
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-ban fa-2x fa-fw has-text-warning"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must have recorded at least <strong>{{ app.min_minutes_required }}</strong> minute<span v-if="app.min_minutes_required > 1">s</span> to apply for this certification.</div>
                                                        <div class="mt-1 is-size-7 has-text-grey">You currently have {{ totalMinutes }} minute<span v-if="totalMinutes > 1">s</span> recorded.</div>
                                                    </div>
                                                </div>
                                                <div class="columns is-gapless" v-else>
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-check-circle fa-2x fa-fw has-text-success"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must have recorded at least <strong>{{ app.min_minutes_required }}</strong> minute<span v-if="app.min_minutes_required > 1">s</span> to apply for this certification.</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div v-if="app.min_sessions_required" class="mb-5">
                                                <div class="columns is-gapless" v-if="sessionCount < app.min_sessions_required">
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-ban fa-2x fa-fw has-text-warning"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must have attended and recorded at least <strong>{{ app.min_sessions_required }}</strong> session<span v-if="app.min_sessions_required > 1">s</span> to apply for this certification.</div>
                                                        <div class="mt-1 is-size-7 has-text-grey">You currently have {{ sessionCount }} session<span v-if="sessionCount > 1">s</span> recorded.</div>
                                                    </div>
                                                </div>
                                                <div class="columns is-gapless" v-else>
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-check-circle fa-2x fa-fw has-text-success"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must have attended and recorded at least <strong>{{ app.min_sessions_required }}</strong> session<span v-if="app.min_sessions_required > 1">s</span> to apply for this certification.</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div v-if="app.req_sessions.length" class="mb-5" v-for="session in app.req_sessions">
                                                <div class="columns is-gapless" v-if="!hasAttendedSession(session)">
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-ban fa-2x fa-fw has-text-warning"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must have attended and recorded <strong>"{{ session.conf_sessions.title }}"</strong> to apply for this certification.</div>
                                                    </div>
                                                </div>
                                                <div class="columns is-gapless" v-else>
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-check-circle fa-2x fa-fw has-text-success"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must have attended and recorded <strong>"{{ session.conf_sessions.title }}"</strong> to apply for this certification.</div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div v-if="app.req_tracks.length" class="mb-5" v-for="track in getRequiredTracks(app)">
                                                <div class="columns is-gapless" v-if="!track.complete">
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-ban fa-2x fa-fw has-text-warning"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must have attended and recorded at least <strong>{{ track.min_sessions_required }}</strong> session<span v-if="track.min_sessions_required > 1">s</span> and at least <strong>{{ track.min_minutes_required }}</strong> minute<span v-if="track.min_minutes_required  > 1">s</span> from the <strong>{{ track.conf_session_tracks.title }}</strong> track to apply for this certification.</div>
                                                        <div class="mt-1 is-size-7 has-text-grey">You currently have {{track.sessionCount}} session<span v-if="track.sessionCount !== 1">s</span> and {{ track.minutes }} minute<span v-if="track.minutes !== 1">s</span> recorded in the {{ track.conf_session_tracks.title }} track.</div>
                                                    </div>
                                                </div>
                                                <div class="columns is-gapless" v-else>
                                                    <div class="column is-narrow"><i class="fal ml-1 mr-3 fa-check-circle fa-2x fa-fw has-text-success"></i></div>
                                                    <div class="column">
                                                        <div class="mt-1">You must have attended and recorded at least <strong>{{ track.min_sessions_required }}</strong> session<span v-if="track.min_sessions_required > 1">s</span> and at least <strong>{{ track.min_minutes_required }}</strong> minute<span v-if="track.min_minutes_required  > 1">s</span> from the <strong>{{ track.conf_session_tracks.title }}</strong> track to apply for this certification.</div>
                                                    </div>
                                                </div>
                                            </div>


                                        </section>
                                    </b-message>
                                </div>

                                <div class="mt-6">
                                    <b-button
                                        size="is-large"
                                        type="is-info"
                                        expanded
                                        :disabled="!applyReady(app) || processing"
                                        @click="apply(app)"
                                        v-if="app.certification.type === 'unit'"
                                    >
                                        <i class="fas fa-arrow-right mr-4"></i>Apply for <strong>{{ calcUnits(app).toFixed(2) }} {{ app.certification.unit_title }}</strong>
                                    </b-button>
                                    <b-button
                                        size="is-large"
                                        type="is-info"
                                        expanded
                                        :disabled="!applyReady(app) || processing"
                                        @click="apply(app)"
                                        v-else
                                    >
                                        <i class="fas fa-arrow-right mr-4"></i>Apply for <strong> {{ app.certification.unit_title }}</strong>
                                    </b-button>
                                </div>
                            </div>
                        </div>

                        <div v-else-if="!getConfApp(app).completed_at">
                            <div class="is-size-6 mt-4 has-text-weight-bold"><i class="fas fa-exclamation-triangle has-text-warning mr-2"></i>Application Incomplete. You are on step {{ getConfApp(app).progress }} of {{ app.outline.length + ((getConfApp(app).quoted_price) ? 2 : 1) }}.</div>
                            <div class="is-size-6 mt-4 mb-2 has-text-grey" v-if="getConfApp(app).review_at"><i class="far fa-fw fa-info-circle mr-2 has-text-warning"></i>You have reached a step that requires further review and approval. Please allow up to 48 hours for this step to be completed.</div>

                            <b-message type="is-warning" v-if="getConfApp(app).rejection_message" class="mt-5">
                                <div class="mb-3 has-text-weight-bold">There is a Problem with Your Application</div>
                                <div>{{ getConfApp(app).rejection_message }}</div>
                            </b-message>

                            <hr/>
                            <b-button :type="(getConfApp(app).review_at) ? 'is-light' : 'is-danger'" tag="router-link" :to="{name: 'application', params: {applicationId: getConfApp(app).id}}"><strong>Continue Application</strong><i class="far fa-arrow-right-long ml-3"></i></b-button>
                        </div>

                        <div v-else>
                            <template v-if="getConfApp(app).is_rejected === 0">
                                <div class="is-size-7 mt-4 has-text-grey"><i class="far fa-fw fa-check-circle mr-2 has-text-success"></i>Certification completed and approved.</div>
                                <hr/>
                                <b-button type="is-success" tag="router-link" :to="{name: 'application', params: {applicationId: getConfApp(app).id}}"><strong>View Certification</strong><i class="far fa-arrow-right-long ml-3"></i></b-button>
                            </template>
                            <template v-else>
                                <b-message type="is-danger" class="mt-5">
                                    <div class="mb-3 has-text-weight-bold">Application Rejected</div>
                                    <div>{{ getConfApp(app).rejection_message }}</div>
                                </b-message>
                            </template>
                        </div>
                    </section>

                </div>
            </div>
        </ui-card>
    </div>
</template>

<script>
    import {client as http} from "../../../http_client";
    import UiCard from "../../../components/UiCard.vue";
    import vueSignature from "vue-signature";
    import LogSignature from "../LogSignature.vue";

    export default {
        components: {LogSignature, UiCard, vueSignature},
        props: ["registrationId"],
        data: function() {
            return {
                registration: null,
                loading: true,
                processing: false,
                appOpen: false
            };
        },
        computed: {
            totalMinutes: function() {
                let minutes = 0;
                let sessions = {};

                for (let i = 0; i < this.registration.reg_record_log.length; i++) {
                    const regRecordLogElement = this.registration.reg_record_log[i];
                    if (!sessions[regRecordLogElement.session_id]) {
                        sessions[regRecordLogElement.session_id] = 0;
                    }
                    sessions[regRecordLogElement.session_id] += regRecordLogElement.minutes;
                }
                for (const id in sessions) {
                    let session = this.registration.conference.sessions.find(session => session.id === parseInt(id));

                    if (session && session.max_log_minutes > 0) {
                        sessions[id] = Math.min(session.max_log_minutes, sessions[id]);
                    }
                    minutes += sessions[id];
                }

                return minutes;
            },
            sessionCount: function() {
                let sessions = [];
                for (let i = 0; i < this.registration.reg_record_log.length; i++) {
                    const log = this.registration.reg_record_log[i];
                    if (log.minutes > 0 && sessions.indexOf(log.session_id) === -1) {
                        sessions.push(log.session_id);
                    }
                }
                return sessions.length;
            }
        },
        methods: {
            loadRegistration: function(force) {
                http.get("app/registrations/" + this.registrationId, {force}).then(response => {
                    let reg = response.data;
                    for (let i = 0; i < reg.conference.applications.length; i++) {
                        const application = reg.conference.applications[i];

                        this.$set(application, "ready", {});

                        application.ready = {
                            sessions: false,
                            ondemand: false,
                            log: false,
                            confirm: false,
                            toggle: false,
                            charge: false
                        };
                    }

                    this.$set(this, "registration", reg);
                    this.loading = false;
                });
            },
            start(app) {
                app.ready.toggle = true;
                this.appOpen = true;
            },
            apply(app) {
                if (!this.processing) {
                    this.processing = true;
                    http.post("app/registrations/" + this.registrationId + "/applications", {conf_application_id: app.id}).then(response => {
                        this.$router.push({name: "application", params: {applicationId: response.data.id}});
                    });
                }
            },
            getConfApp: function(conf_app) {
                for (let i = 0; i < this.registration.applications.length; i++) {
                    const app = this.registration.applications[i];
                    if (app.conf_application_id === conf_app.id) {
                        return app;
                    }
                }
                return {};
            },
            scrollTo(refName) {
                let element = this.$refs[refName];
                if (element) {
                    element.scrollIntoView({behavior: "smooth"});
                }
            },
            calcUnits(app) {
                let units = this.totalMinutes / app.certification.unit_ratio;
                if (app.certification.unit_round_to) {
                    units = Math.floor(this.totalMinutes / app.certification.unit_round_to) * app.certification.unit_round_to;
                }
                return Math.min(app.max_units, units);
            },
            chargeAmount(app) {
                let flatPrice = parseFloat(app.flat_price);
                let unitPrice = parseFloat(app.per_unit_price);
                if (flatPrice) {
                    return flatPrice;
                } else if (unitPrice) {
                    return this.calcUnits(app) * unitPrice;
                } else {
                    return 0;
                }
            },
            applyReady(app) {
                if (app.min_minutes_required) {
                    app.ready.minMinutes = this.totalMinutes >= app.min_minutes_required;
                } else {
                    app.ready.minMinutes = true;
                }

                if (app.min_sessions_required) {
                    app.ready.minSessions = this.sessionCount >= app.min_sessions_required;
                } else {
                    app.ready.minSessions = true;
                }

                let reqSessions = true;
                for (let i = 0; i < app.req_sessions.length; i++) {
                    const session = app.req_sessions[i];

                    if (!this.hasAttendedSession(session)) {
                        reqSessions = false;
                        break;
                    }
                }

                let reqTracks = true;
                for (let i = 0; i < app.req_tracks.length; i++) {
                    const track = app.req_tracks[i];
                    if (!track.complete) {
                        reqTracks = false;
                        break;
                    }
                }

                return reqTracks && reqSessions && app.ready.sessions && app.ready.ondemand && app.ready.log && app.ready.confirm && app.ready.minSessions && app.ready.minMinutes && (this.chargeAmount(app) === 0 || (this.chargeAmount(app) > 0 && app.ready.charge));
            },
            hasAttendedSession(session) {
                for (let i = 0; i < this.registration.reg_record_log.length; i++) {
                    const log = this.registration.reg_record_log[i];
                    if (log.session_id === session.conf_session_id) {
                        return true;
                    }
                }
                return false;
            },
            getRequiredTracks(app) {
                let req_tracks = app.req_tracks;

                if (req_tracks.length) {
                    for (let i = 0; i < this.registration.reg_record_log.length; i++) {
                        let log = this.registration.reg_record_log[i];
                        log.session = this.registration.conference.sessions.find(session => session.id === log.session_id);
                    }
                }

                for (let i = 0; i < req_tracks.length; i++) {
                    let track = req_tracks[i];
                    let sessions = this.registration.reg_record_log.filter(log => log.session.track_id === track.conf_session_track_id);
                    track.sessions = sessions;
                    track.minutes = 0;
                    track.sessionCount = 0;

                    for (let j = 0; j < sessions.length; j++) {
                        const session = sessions[j];
                        track.minutes += session.minutes;
                        if (session.minutes > 0) {
                            track.sessionCount++;
                        }
                    }
                }

                for (let i = 0; i < req_tracks.length; i++) {
                    let track = req_tracks[i];
                    if (track.sessionCount >= track.min_sessions_required && track.minutes >= track.min_minutes_required) {
                        track.complete = true;
                    } else {
                        track.complete = false;
                    }
                }

                return req_tracks;
            },
        },
        mounted() {
            this.loadRegistration(true);
        }
    };
</script>

<style lang="scss" scoped>

    .log-row {
        .log-title {
            margin-top: 2px;
            font-size: 1em;
            font-weight: bold;
        }

        .log-date {
            margin-top: 5px;
            font-size: 0.8em;
        }
    }

    h4 {
        font-size: 1.2rem;
        margin-top: 1.5rem;
        margin-bottom: 2rem;
        padding-bottom: 0.8rem;

        i {
            color: #336699;
        }

        border-bottom: 1px dashed #ddd;
    }
</style>

<style lang="scss">
    .step-item {
        list-style: none;
    }

    .survey {
        .survey-radio {
            margin: 0 !important;
            padding: 0 !important;
        }
    }
</style>
